import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Users, Globe, ArrowRight, TrendingUp, Target, Award, Building } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export default function ImpactPage() {
  return (
    <div data-testid="impact-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-white/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Global Impact Highlights
            </h1>
            <p className="text-xl text-slate-800">
              Measuring our progress in transforming communities worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#7BC4E2]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E5B75F]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-slate-700 leading-relaxed text-lg mb-4">
              Across continents and communities, the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong> continues to deliver measurable, community-driven impact through programmes that address poverty, inequality, climate vulnerability, and social exclusion.
            </p>
            <p className="text-slate-700 leading-relaxed text-lg">
              Through our regional offices and field implementation hubs in <span className="text-[#7BC4E2] font-semibold">Europe, Africa, Asia, North America, and South America</span>, OPAD works collaboratively with local stakeholders, development partners, and international institutions to implement sustainable solutions that improve lives and strengthen resilience among underserved populations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#E08B7A]/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#4A7C59]/10 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Our Impact at a Glance
            </h2>
            <div className="w-24 h-1 bg-[#E08B7A] mx-auto rounded-full"></div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl text-center shadow-lg border border-slate-100"
              {...fadeInUp}
            >
              <TrendingUp className="w-10 h-10 text-[#7BC4E2] mx-auto mb-3" />
              <p className="text-3xl md:text-4xl font-bold text-[#7BC4E2] font-['Manrope']">12,000+</p>
              <p className="text-slate-600 mt-2 text-sm">Beneficiaries Reached</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl text-center shadow-lg border border-slate-100"
              {...fadeInUp}
              transition={{ delay: 0.1 }}
            >
              <Target className="w-10 h-10 text-[#E08B7A] mx-auto mb-3" />
              <p className="text-3xl md:text-4xl font-bold text-[#E08B7A] font-['Manrope']">35+</p>
              <p className="text-slate-600 mt-2 text-sm">Active Projects</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl text-center shadow-lg border border-slate-100"
              {...fadeInUp}
              transition={{ delay: 0.2 }}
            >
              <Building className="w-10 h-10 text-[#4A7C59] mx-auto mb-3" />
              <p className="text-3xl md:text-4xl font-bold text-[#4A7C59] font-['Manrope']">50+</p>
              <p className="text-slate-600 mt-2 text-sm">Partner Organizations</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl text-center shadow-lg border border-slate-100"
              {...fadeInUp}
              transition={{ delay: 0.3 }}
            >
              <Users className="w-10 h-10 text-[#E5B75F] mx-auto mb-3" />
              <p className="text-3xl md:text-4xl font-bold text-[#E5B75F] font-['Manrope']">300+</p>
              <p className="text-slate-600 mt-2 text-sm">Volunteers Mobilized</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl text-center shadow-lg border border-slate-100 col-span-2 md:col-span-1"
              {...fadeInUp}
              transition={{ delay: 0.4 }}
            >
              <Globe className="w-10 h-10 text-[#7BC4E2] mx-auto mb-3" />
              <p className="text-3xl md:text-4xl font-bold text-[#7BC4E2] font-['Manrope']">80+</p>
              <p className="text-slate-600 mt-2 text-sm">Communities Served</p>
            </motion.div>
          </div>
          
          {/* Detailed Stats */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#7BC4E2] shadow-lg"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h4 className="font-bold text-[#7BC4E2] mb-2">12,000+ Beneficiaries Reached</h4>
              <p className="text-slate-600 text-sm">Supporting individuals and families through education, digital inclusion, livelihood development, and humanitarian assistance programmes.</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#E08B7A] shadow-lg"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="font-bold text-[#E08B7A] mb-2">35+ Active Projects</h4>
              <p className="text-slate-600 text-sm">Implementing development and emergency response initiatives across multiple regions in alignment with the Sustainable Development Goals (SDGs).</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#4A7C59] shadow-lg"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="font-bold text-[#4A7C59] mb-2">50+ Partner Organizations</h4>
              <p className="text-slate-600 text-sm">Collaborating with civil society organizations, academic institutions, municipalities, and international development partners.</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#E5B75F] shadow-lg"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h4 className="font-bold text-[#E5B75F] mb-2">300+ Volunteers Mobilized</h4>
              <p className="text-slate-600 text-sm">Engaging local and international volunteers in programme delivery, training, outreach, and field coordination activities.</p>
            </motion.div>
            <motion.div 
              className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-[#7BC4E2] shadow-lg md:col-span-2"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <h4 className="font-bold text-[#7BC4E2] mb-2">80+ Communities Served</h4>
              <p className="text-slate-600 text-sm">Reaching vulnerable urban and rural communities through inclusive, locally led interventions.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sustainable Development */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#4A7C59]/10 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#7BC4E2]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="bg-white/90 backdrop-blur-sm p-8 md:p-12 rounded-3xl shadow-xl border border-slate-100 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-6 text-center">
              Driving Sustainable Development Through Local Action
            </h2>
            <p className="text-slate-700 leading-relaxed text-center mb-8 text-lg">
              From youth empowerment initiatives in <strong className="text-[#E08B7A]">East Africa</strong> to community-based food distribution programmes in <strong className="text-[#7BC4E2]">Botkyrka, Sweden</strong>, and digital capacity-building projects implemented through <strong className="text-[#4A7C59]">YIBO – Sweden</strong>, OPAD continues to expand its reach by delivering impactful programmes that promote opportunity, resilience, and inclusive development outcomes worldwide.
            </p>
            <div className="text-center border-t border-slate-200 pt-8">
              <p className="text-2xl md:text-3xl font-bold font-['Manrope'] text-[#E08B7A] mb-6">
                Together, We Are Creating Lasting Change
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-8 py-6 text-lg font-semibold">
                    <Heart className="w-5 h-5 mr-2" />
                    Donate Now
                  </Button>
                </a>
                <Link to="/get-involved">
                  <Button className="bg-[#4A7C59] hover:bg-[#3A6B49] text-white rounded-full px-8 py-6 text-lg font-semibold">
                    <Users className="w-5 h-5 mr-2" />
                    Get Involved
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
